import React, { useContext, useEffect, useMemo, useState } from 'react';
import { SiteContext } from '../../contexts/SiteContext';
import { api } from '../../lib/api';
import { useTranslation } from 'react-i18next';

export default function Settings() {
  const { t } = useTranslation();
  const { website } = useContext(SiteContext);
  const websiteId = website?._id;


  
  if (!websiteId) {
    return (
      <div className="p-4 bg-white rounded border shadow">
        {t('websiteNotLoaded')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('settings.title')}</h1>
      <InviteAdminsCard websiteId={websiteId} />
    </div>
  );
}



function InviteAdminsCard({ websiteId }) {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');

  const [invites, setInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [errInvites, setErrInvites] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoadingInvites(true);
        setErrInvites('');
        const { data } = await api.get(`/websites/${websiteId}/invitations`);
        if (!alive) return;
        setInvites(data.map(normalizeInvite));
      } catch {
        if (!alive) return;
        setErrInvites(t('feedback.failedToLoadInvites'));
      } finally {
        alive && setLoadingInvites(false);
      }
    })();
    return () => { alive = false; };
  }, [websiteId, t]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setErr(''); setOk('');
    if (!email.trim()) {
      setErr(t('form.emailRequired'));
      return;
    }
    setSending(true);
    try {
      // Always admin invites for now
      const { data } = await api.post(`/websites/${websiteId}/invitations`, { email, role: 'admin' });
      setInvites(prev => [normalizeInvite(data), ...prev]);
      setEmail('');
      setOk(t('feedback.invitationSent'));
    } catch (e) {
      setErr(e?.response?.data?.msg || t('feedback.failedToSendInvite'));
    } finally {
      setSending(false);
    }
  };

  const resend = async (inviteId) => {
  setErr(''); setOk('');
  try {
    await api.post(`/websites/${websiteId}/invitations/${inviteId}/resend`);
    setOk(t('feedback.invitationResent'));
  } catch (e) {
    setErr(e?.response?.data?.msg || t('feedback.failedToResend'));
  }
};

const revoke = async (inviteId) => {
  setErr(''); setOk('');
  const prev = invites;
  setInvites(p => p.filter(i => String(i._id) !== String(inviteId)));
  try {
    await api.delete(`/websites/${websiteId}/invitations/${inviteId}`);
    setOk(t('feedback.invitationRevoked'));
  } catch (e) {
    setErr(e?.response?.data?.msg || t('feedback.failedToRevoke'));
    setInvites(prev);
  }
}

function normalizeInvite(i) {
  return {
    _id: i._id ?? i.id,              // unify id
    email: i.email,
    role: i.role,
    createdAt: i.createdAt ?? i.created_at ?? null,
    acceptedAt: i.acceptedAt ?? i.accepted_at ?? null,
    revokedAt: i.revokedAt ?? i.revoked_at ?? null,
    token: i.token ?? null
  };
};
  const sorted = useMemo(() => [...invites].sort((a,b) =>
    new Date(b.createdAt ?? 0) - new Date(a.createdAt ?? 0)
  ), [invites]);

  return (
    <section className="bg-white border rounded shadow p-4">
      <header className="mb-4">
        <h2 className="text-xl font-semibold">{t('settings.inviteAdmins.title')}</h2>
        <p className="text-gray-600 text-sm">
          {t('settings.inviteAdmins.subtitle')}
        </p>
      </header>

      <form onSubmit={handleInvite} className="grid gap-3 md:grid-cols-[1fr_180px]">
        <div>
          <label className="block text-sm font-medium mb-1">{t('form.emailLabel')}</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('form.emailPlaceholder')}
            className="w-full border rounded px-3 py-2 outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={sending}
            className="w-full bg-blue-600 text-white rounded px-4 py-2 disabled:opacity-60"
          >
            {sending ? t('form.sending') : t('form.sendInvite')}
          </button>
        </div>
      </form>

      {(err || ok) && (
        <div className="mt-3">
          {err && <div className="text-red-600 text-sm">{err}</div>}
          {ok && <div className="text-green-700 text-sm">{ok}</div>}
        </div>
      )}

      <hr className="my-6" />
      <h3 className="font-semibold mb-2">{t('settings.inviteAdmins.pendingTitle')}</h3>

      {loadingInvites ? (
        <div>{t('loading')}</div>
      ) : errInvites ? (
        <div className="text-red-600">{errInvites}</div>
      ) : sorted.length ? (
        <div className="overflow-auto">
          <table className="min-w-[640px] w-full border">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="p-2 border">{t('table.email')}</th>
                <th className="p-2 border">{t('table.status')}</th>
                <th className="p-2 border">{t('table.sent')}</th>
                <th className="p-2 border">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(inv => (
                <tr key={inv._id} className="hover:bg-gray-50">
                  <td className="p-2 border">{inv.email}</td>
                  <td className="p-2 border">
                    {inv.acceptedAt
                      ? t('emailStatus.accepted')
                      : inv.revokedAt
                        ? t('emailStatus.revoked')
                        : t('emailStatus.pending')}
                  </td>
                  <td className="p-2 border">
                    {inv.createdAt ? new Date(inv.createdAt).toLocaleString() : '—'}
                  </td>
                  <td className="p-2 border">
                    {!inv.acceptedAt && !inv.revokedAt && (
                      <>
                        <button
                          className="text-blue-700 underline mr-3"
                          onClick={() => resend(inv._id)}
                        >
                          {t('actions.resend')}
                        </button>
                        <button
                          className="text-red-700 underline"
                          onClick={() => revoke(inv._id)}
                        >
                          {t('actions.revoke')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-sm text-gray-600">{t('empty.noPendingInvites')}</div>
      )}
    </section>
  );
}
